import { useState, useEffect, useCallback } from 'react';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  timestamp: string;
  read: boolean;
  link?: string;
  metadata?: Record<string, any>;
  isGroup?: boolean;
  groupName?: string;
}

export const useNotifications = () => {
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [username, setUsername] = useState<string>('');

  useEffect(() => {
    identityApi.getProfileInfo()
      .then(p => p.email && setUsername(p.email))
      .catch(console.error);
  }, [identityApi]);

  const fetchNotifications = useCallback(async () => {
    if (!username) return;
    
    setLoading(true);
    setError(undefined);
    
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      
      const response = await fetch(`${baseUrl}/notifications?username=${encodeURIComponent(username)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch notifications: ${response.status}`);
      }

      const data = await response.json();
      setNotifications(data.notifications || []);
    } catch (e: any) {
      setError(e.message);
      console.error('Failed to fetch notifications:', e);
    } finally {
      setLoading(false);
    }
  }, [username, discoveryApi, identityApi]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!username) return;

    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const response = await fetch(`${baseUrl}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
      });

      if (response.ok) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
      }
    } catch (e) {
      console.error('Failed to mark notification as read:', e);
    }
  }, [username, discoveryApi, identityApi]);

  const markAllAsRead = useCallback(async () => {
    if (!username) return;

    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const response = await fetch(`${baseUrl}/notifications/read-all`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username }),
      });

      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      }
    } catch (e) {
      console.error('Failed to mark all notifications as read:', e);
    }
  }, [username, discoveryApi, identityApi]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!username) return;

    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const response = await fetch(`${baseUrl}/notifications/${notificationId}?username=${encodeURIComponent(username)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
      }
    } catch (e) {
      console.error('Failed to delete notification:', e);
    }
  }, [username, discoveryApi, identityApi]);

  const deleteAllNotifications = useCallback(async () => {
    if (!username) return;

    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const response = await fetch(`${baseUrl}/notifications/delete-all?username=${encodeURIComponent(username)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setNotifications([]);
      }
    } catch (e) {
      console.error('Failed to delete all notifications:', e);
    }
  }, [username, discoveryApi, identityApi]);

  // Initial fetch
  useEffect(() => {
    if (username) {
      fetchNotifications();
    }
  }, [username, fetchNotifications]);

  // Smart polling for notifications with optimizations to reduce server load
  useEffect(() => {
    if (!username) return;

    let pollInterval: NodeJS.Timeout | null = null;
    let currentInterval = 15000; // Start with 15 seconds
    const minInterval = 15000; // Minimum 15 seconds
    const maxInterval = 60000; // Maximum 60 seconds (1 minute)
    let lastNotificationCount = notifications.length;

    const startPolling = () => {
      pollInterval = setInterval(async () => {
        // Only poll if page is visible
        if (document.hidden) {
          return;
        }

        await fetchNotifications();

        // Adaptive interval: if notifications changed, poll faster; otherwise, slow down
        if (notifications.length !== lastNotificationCount) {
          // New notifications - keep polling frequently
          currentInterval = minInterval;
          lastNotificationCount = notifications.length;
          
          // Restart interval with faster rate
          if (pollInterval) clearInterval(pollInterval);
          startPolling();
        } else {
          // No changes - gradually slow down polling
          if (currentInterval < maxInterval) {
            currentInterval = Math.min(currentInterval * 1.5, maxInterval);
            if (pollInterval) clearInterval(pollInterval);
            startPolling();
          }
        }
      }, currentInterval);
    };

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page hidden - stop polling
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      } else {
        // Page visible - resume polling and fetch immediately
        fetchNotifications();
        if (!pollInterval) {
          currentInterval = minInterval; // Reset to fast polling
          startPolling();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    startPolling();

    return () => {
      if (pollInterval) clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [username, fetchNotifications, notifications.length]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    notifications,
    loading,
    error,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
  };
};

